import { DOCUMENT } from '@angular/common';
import { Component, Inject, PLATFORM_ID } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { NgbRatingConfig } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngxs/store';
import { filter } from 'rxjs';
import { PixelService } from './shared/service/pixel.service';
import { QueryParamDecoratorService } from "./shared/service/query-param.decorator.service";
import { LanguageService } from './shared/service/language.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
})
export class AppComponent {
  title = 'template2';

  constructor(
    @Inject(DOCUMENT) document: Document,
    config: NgbRatingConfig,
    public meta: Meta,
    private store: Store,
    @Inject(PLATFORM_ID) private platformId: Object,
    private router: Router,
    private pixelSvc: PixelService,
    private queryParamDecoratorService: QueryParamDecoratorService,
    private languageService: LanguageService
  ) {
    config.max = 5;
    config.readonly = true;

    // NOTA: no llamamos aquí a ningún switch de idioma "provisional". El idioma inicial de
    // UI lo carga (bloqueante, antes del render) el APP_INITIALIZER de app.config.ts, y el
    // idioma de CONTENIDO definitivo lo resuelve LanguageService una sola vez cuando llegan
    // los settings del site (resolveInitialLanguage). Inyectar el servicio aquí basta para
    // instanciarlo pronto y activar sus streams (dirección reactiva + resolución inicial).
    // Antes había un initializeLanguage() extra que lanzaba un translate.use() asíncrono en
    // paralelo: al resolverse tarde (carga del JSON) pisaba el idioma final y dejaba el `dir`
    // descolgado (idioma es + rtl). Eliminado para quitar la carrera.
    void this.languageService;

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: any) => {
      this.trackPageViews();
    });


  }
  private trackPageViews() {

    if (!this.pixelSvc.facebookPixelExist && !this.pixelSvc.tiktokPixelExist && !this.pixelSvc.googleAnalyticsExist)
      return
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: any) => {


      switch (true) {
        case event.urlAfterRedirects.includes('/home'):
          this.pixelSvc.trackFbPixel("ViewContent", { content_name: 'Home Page' });
          this.pixelSvc.trackTiktokPixel("ViewContent", { content_name: 'Home Page' });

          // GOOGLE ANALYTICS
          if (this.pixelSvc.googleAnalyticsExist) {
            this.pixelSvc.trackGoogleAnalytics('view_item', { content_name: 'Home Page' });
          }
          break;
        case event.urlAfterRedirects.includes('/products'):

          if (!event.url.includes("categoryId")) {

            const productId = extractProductId(event.url);
            if (productId) {
              this.pixelSvc.trackFbPixel("ViewContent", { content_name: "products details", content_type: "products", content_ids: [productId] });
              this.pixelSvc.trackTiktokPixel("ViewContent", { content_name: "products details", content_type: "products", content_ids: [productId] });

              // GOOGLE ANALYTICS
              if (this.pixelSvc.googleAnalyticsExist) {
                this.pixelSvc.trackGoogleAnalytics('view_item', {
                  items: [{ item_id: productId, item_name: "products details" }]
                });
              }
            } else {
              this.pixelSvc.trackFbPixel("ViewContent", { content_name: "products list", content_type: "products" });
              this.pixelSvc.trackTiktokPixel("ViewContent", { content_name: "products list", content_type: "products" });

              // GOOGLE ANALYTICS
              if (this.pixelSvc.googleAnalyticsExist) {
                this.pixelSvc.trackGoogleAnalytics('view_item_list', { item_list_name: "products list" });
              }
            }


          }
          if (event.url.includes("categoryId")) {
            this.pixelSvc.trackFbPixel("ViewContent", { content_name: "products list", content_type: "categories", content_ids: extractCategoryIds(event.url) });
            this.pixelSvc.trackTiktokPixel("ViewContent", { content_name: "products list", content_type: "categories", content_ids: extractCategoryIds(event.url) });

            // GOOGLE ANALYTICS
            if (this.pixelSvc.googleAnalyticsExist) {
              this.pixelSvc.trackGoogleAnalytics('view_item_list', {
                item_list_name: "products list",
                items: extractCategoryIds(event.url).map(id => ({ item_id: id, item_name: "category" }))
              });
            }
          }


          this.pixelSvc.trackFbPixel("ViewContent", { content_name: 'Products Page' });
          this.pixelSvc.trackTiktokPixel("ViewContent", { content_name: 'Products Page' });

          // GOOGLE ANALYTICS
          if (this.pixelSvc.googleAnalyticsExist) {
            this.pixelSvc.trackGoogleAnalytics('page_view', { page_title: 'Products Page' });
          }
          break;
        case event.urlAfterRedirects.includes('/checkout'):
          this.pixelSvc.trackFbPixel('InitiateCheckout');
          this.pixelSvc.trackTiktokPixel('InitiateCheckout');

          // GOOGLE ANALYTICS
          if (this.pixelSvc.googleAnalyticsExist) {
            this.pixelSvc.trackGoogleAnalytics('begin_checkout', {});
          }
          break;
        default:
          this.pixelSvc.trackFbPixel('PageView');
          this.pixelSvc.trackTiktokPixel('PageView');

          // GOOGLE ANALYTICS
          if (this.pixelSvc.googleAnalyticsExist) {
            this.pixelSvc.trackGoogleAnalytics('page_view', {});
          }
          break;
      }



    });
    // extract categories id from url
    function extractCategoryIds(inputString: string) {
      // Regular expression to match categoryId values
      const categoryIdPattern = /categoryId=([a-zA-Z0-9]+)/g;

      // Array to store the extracted categoryId values
      const categoryIds = [];
      let match;

      // Use the pattern to find all matches
      while ((match = categoryIdPattern.exec(inputString)) !== null) {
        categoryIds.push(match[1]);
      }

      return categoryIds;
    }

    //extract productId from url
    function extractProductId(url: string) {
      // Regular expression to match the productId in the URL
      const productIdPattern = /\/products\/([a-zA-Z0-9]+)$/;

      // Use the pattern to find a match in the URL
      const match = url.match(productIdPattern);

      // If a match is found, return the productId; otherwise, return null
      return match ? match[1] : null;
    }

  }



}
